import React from 'react';

import Button from '../../../../library/button/button';
import Tooltip from '../../../../library/tooltip/tooltip';
import Typography from '../../../../library/typography/typography';
import ProductAvailability from '../../../../components/product-availability/product-availability';
import {
  MapMarkerAltSolidIcon,
  ScrewdriverWrenchRegularIcon,
  ShopSolidIcon,
  TruckSolidIcon
} from '../../../../shared/icons/icons';

import {
  useAemContent,
  useFeatureFlags,
  useMyStore
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { getPdpMultiStoreDataLayerValues } from '../../utils/product-details-page-analytics-utils';

import {
  BundleData,
  ProductData,
  StockData
} from '../../../../shared/types/graphql-types';
import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../../../shared/constants/cms';
import { FEATURE_FLAGS } from '../../../../shared/constants/feature-flags';

import './store-availability-options.scss';

// Remove when OW-17564 is merged
interface ProductDataWithNearbyStoreStock extends ProductData {
  nearByStoreStock: StockData;
}

interface StoreAvailabilityOptionsProps {
  myStoreCode?: string | null;
  onClickStore: (storeCode: string) => void;
  prebuiltPackage?: BundleData;
  product: ProductDataWithNearbyStoreStock;
  quantity?: number;
  rearProduct?: ProductData | null;
  rearQuantity?: number;
  selectedStoreCode?: string | null;
}

function StoreAvailabilityOptions(props: StoreAvailabilityOptionsProps) {
  const {
    myStoreCode,
    onClickStore,
    prebuiltPackage,
    product,
    quantity,
    rearProduct,
    rearQuantity,
    selectedStoreCode
  } = props;

  const nearbyStoreCode = product?.nearByStoreStock?.storeCode;
  const { myStore } = useMyStore();
  const { featureFlags } = useFeatureFlags();

  const { aemTooltipsContent } = useAemContent(AEM_CONTENT_PAGE_IDS.global);

  const inStockTooltip =
    aemTooltipsContent?.[AEM_CONTENT_IDS.inStockNearbyStore]?.tipText;

  const shouldShowInStockBanner =
    featureFlags?.[FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__] &&
    product?.productType === 'Tire' &&
    product?.stock?.availabilityMessageRank !== 1 &&
    (product?.nearByStoreStock?.availabilityMessageRank === 1 ||
      !product?.nearByStoreStock?.availabilityMessageRank);

  if (!myStoreCode) return null;

  function onClickSelectedStoreAnalytics(storeCode) {
    const payload = getPdpMultiStoreDataLayerValues(
      product,
      myStore?.nearByStores,
      myStoreCode,
      storeCode
    );
    trackAction('pdp_multistore_select_store', {
      ...payload
    });
  }

  function handleOnClickPreferredStore() {
    myStoreCode && onClickStore(myStoreCode);
    if (selectedStoreCode !== myStoreCode) {
      onClickSelectedStoreAnalytics(myStoreCode);
    }
  }

  function handleOnClickNearByStore() {
    nearbyStoreCode && onClickStore(nearbyStoreCode);
    if (selectedStoreCode !== nearbyStoreCode) {
      onClickSelectedStoreAnalytics(nearbyStoreCode);
    }
  }

  return (
    <div
      styleName={`container ${
        !nearbyStoreCode ? 'container--single-column' : ''
      } ${
        featureFlags?.[FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__]
          ? 'container--items-end'
          : ''
      }`}
    >
      <Button
        onClick={handleOnClickPreferredStore}
        styleName={`option ${
          selectedStoreCode === myStoreCode ? 'option--selected' : ''
        } ${
          featureFlags?.[FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__]
            ? 'option--no-padding'
            : ''
        }`}
      >
        <div
          styleName={`${
            featureFlags?.[FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__]
              ? 'option-content'
              : ''
          }`}
        >
          <Typography styleName="option-title" tag="div" weight="heavy">
            <MapMarkerAltSolidIcon styleName="my-store-icon" />
            My Preferred Store
          </Typography>

          <ProductAvailability
            iconAvailable={
              featureFlags?.[FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__] && (
                <TruckSolidIcon styleName="truck-icon" />
              )
            }
            prebuiltPackage={prebuiltPackage}
            product={product}
            quantity={quantity}
            rearProduct={rearProduct}
            rearQuantity={rearQuantity}
            styleName={`${
              featureFlags?.[FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__] !==
              true
                ? 'product-availability'
                : ''
            }`}
          />
        </div>
      </Button>

      {nearbyStoreCode && (
        <Button
          onClick={handleOnClickNearByStore}
          styleName={`option ${
            selectedStoreCode === nearbyStoreCode ? 'option--selected' : ''
          }  ${
            featureFlags?.[FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__]
              ? 'option--no-padding'
              : ''
          }`}
        >
          {shouldShowInStockBanner && (
            <div styleName="option-banner">
              <ScrewdriverWrenchRegularIcon styleName="screwdriver-wrench-icon" />
              Shop <strong>in-stock tires</strong> for{' '}
              <strong>same-day install</strong>
              <Tooltip content={inStockTooltip} styleName="tooltip" />
            </div>
          )}
          <div
            styleName={`${
              featureFlags?.[FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__]
                ? 'option-content'
                : ''
            }`}
          >
            <Typography styleName="option-title" tag="div" weight="heavy">
              <ShopSolidIcon styleName="nearby-store-icon" />
              Nearby Store
            </Typography>

            <ProductAvailability
              prebuiltPackage={prebuiltPackage}
              product={product}
              quantity={quantity}
              rearProduct={rearProduct}
              rearQuantity={rearQuantity}
              shouldShowNearbyStoreStock
              styleName={`${
                featureFlags?.[FEATURE_FLAGS.__FF_COST_TO_SERVE_IN_STOCK__] !==
                true
                  ? 'product-availability'
                  : ''
              }`}
            />
          </div>
        </Button>
      )}
    </div>
  );
}

export default StoreAvailabilityOptions;
